import { Dispatch } from 'redux'
import { setSessionResumeShowModal } from '../store/sessionResume.actions'
import Logger from '../../../../services/logger.service'

class SessionResumeService {
  private timeoutId: ReturnType<typeof setTimeout> | null = null
  private dispatch: Dispatch | null = null
  private sessionDuration: number | null = null
  private expiresAt: number | null = null
  private lastExtendTime: number | null = null
  private static readonly EXTEND_THRESHOLD_MS = 120 * 1000

  get isRunning(): boolean {
    return this.timeoutId !== null
  }

  initialize(dispatch: Dispatch, sessionDuration: number) {
    this.dispatch = dispatch
    this.sessionDuration = Number(process.env.REACT_APP_SESSION_TIMEOUT_DURATION_MINUTES) || sessionDuration
  }

  startSession() {
    Logger.info('[SESSION_TIMEOUT] Starting session')
    if (!this.dispatch || !this.sessionDuration) {
      Logger.error('[SESSION_TIMEOUT] Service not initialized, cannot start session')
      return
    }
    this.clearTimer()
    const sessionDurationMs = this.sessionDuration * 60 * 1000
    this.expiresAt = Date.now() + sessionDurationMs
    this.lastExtendTime = null
    this.scheduleModal(sessionDurationMs)
  }

  extendSession() {
    if (!this.expiresAt || !this.sessionDuration || !this.dispatch) return
    if (this.lastExtendTime && Date.now() - this.lastExtendTime < SessionResumeService.EXTEND_THRESHOLD_MS) return

    const sessionDurationMs = this.sessionDuration * 60 * 1000
    const prevExpiry = new Date(this.expiresAt).toLocaleTimeString()
    this.lastExtendTime = Date.now()
    this.clearTimer()
    this.expiresAt = Date.now() + sessionDurationMs
    const newExpiry = new Date(this.expiresAt).toLocaleTimeString()
    Logger.info(`[SESSION_EXTEND] Session extended. Previous expiry: ${prevExpiry}, New expiry: ${newExpiry}`)
    this.scheduleModal(sessionDurationMs)
  }

  handleForeground() {
    if (!this.expiresAt || !this.dispatch) return
    const remaining = this.expiresAt - Date.now()
    this.clearTimer()
    this.scheduleModal(remaining)
  }

  private scheduleModal(delayMs: number) {
    if (!this.dispatch) return

    if (delayMs <= 0) {
      Logger.info('[SESSION_TIMEOUT] Session duration reached, showing modal immediately')
      this.showModal()
      return
    }

    Logger.info(`[SESSION_TIMEOUT] Scheduling modal in ${delayMs / 60000} minutes`)
    this.timeoutId = setTimeout(() => {
      this.showModal()
    }, delayMs)
  }

  private showModal() {
    if (!this.dispatch) return
    Logger.info('[SESSION_TIMEOUT] Showing session resume modal')
    this.dispatch(setSessionResumeShowModal(true))
  }

  stopSession() {
    Logger.info('[SESSION_TIMEOUT] Stopping session')
    this.expiresAt = null
    this.lastExtendTime = null
    this.clearTimer()
    this.dispatch?.(setSessionResumeShowModal(false))
  }

  clearScheduledTimeout() {
    Logger.info('[SESSION_TIMEOUT] Clearing scheduled timeout without hiding modal')
    this.clearTimer()
  }

  private clearTimer() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
      this.timeoutId = null
    }
  }
}

const sessionResumeService = new SessionResumeService()
export default sessionResumeService
